import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  VStack,
  useToast,
  Text,
  FormErrorMessage,
  Flex,
  SimpleGrid,
} from '@chakra-ui/react';
import { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { formatPhoneNumber, formatPhoneNumberForDisplay } from '../utils/phoneUtils';
import unifiedEmailService from '../services/unifiedEmailService';

const MemberModal = ({ isOpen, onClose, member, onSave, clubName }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    email: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      if (member) {
        // For existing member, keep the phone number as is
        setFormData({
          firstName: member.first_name || '',
          lastName: member.last_name || '',
          phoneNumber: member.phone_number?.replace(/^\+1/, '') || '',
          email: member.email || '',
        });
      } else {
        setFormData({
          firstName: '',
          lastName: '',
          phoneNumber: '',
          email: '',
        });
      }
      setErrors({});
    }
  }, [isOpen, member]);

  const formatPhoneNumber = (value) => {
    // Remove any non-digits and ensure we only take the last 10 digits
    const digits = value.replace(/\D/g, '').slice(-10);
    // Add +1 prefix for storage
    return `+1${digits}`;
  };

  const validateEmail = (email) => {
    // Basic format check
    const formatRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!formatRegex.test(email)) {
      return 'Invalid email format';
    }

    // Additional checks
    const parts = email.split('@');
    if (parts[0].length > 64) {
      return 'Local part of email cannot exceed 64 characters';
    }
    
    if (email.length > 254) {
      return 'Email address is too long';
    }

    if (parts[0].startsWith('.') || parts[0].endsWith('.')) {
      return 'Local part cannot start or end with a period';
    }

    if (parts[0].includes('..')) {
      return 'Local part cannot contain consecutive periods';
    }

    // Valid email
    return '';
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName) newErrors.firstName = 'First name is required';
    if (!formData.lastName) newErrors.lastName = 'Last name is required';
    
    // Clean and validate phone number
    const cleanPhone = formData.phoneNumber.replace(/\D/g, '');
    if (!cleanPhone || cleanPhone.length !== 10) {
      newErrors.phoneNumber = 'Please enter a 10-digit phone number';
    }
    
    // Email validation
    if (!formData.email) {
      newErrors.email = 'Email address is required';
    } else {
      const emailError = validateEmail(formData.email);
      if (emailError) {
        newErrors.email = emailError;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phoneNumber') {
      // Only allow digits and limit to 10 characters
      const numbersOnly = value.replace(/\D/g, '').slice(0, 10);
      setFormData(prev => ({ ...prev, phoneNumber: numbersOnly }));
    } else if (name === 'email') {
      // Trim whitespace from email as it's typed
      setFormData(prev => ({ ...prev, [name]: value.trim() }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    // Clear error when field is modified
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    
    if (loading) return;
    console.log('Starting submit...', { member, formData });

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      // Format phone number consistently
      const cleanPhone = formData.phoneNumber.replace(/\D/g, '');
      const formattedPhone = cleanPhone.length === 10 ? `+1${cleanPhone}` : formData.phoneNumber;

      // For existing members, if phone hasn't changed, use the original
      if (member && member.phone_number === formattedPhone) {
        console.log('Phone number unchanged');
      } else if (!formattedPhone.match(/^\+1\d{10}$/)) {
        setErrors(prev => ({
          ...prev,
          phoneNumber: 'Please enter a valid 10-digit US phone number'
        }));
        return;
      }

      const contactData = {
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
        phone_number: formattedPhone,
        email: formData.email.trim(),
      };

      let savedData;
      if (member) {
        console.log('Updating existing member...', { id: member.id, contactData });
        
        // Compare with original data
        const oldData = {
          first_name: member.first_name,
          last_name: member.last_name,
          phone_number: member.phone_number,
          email: member.email,
        };

        // Track which fields changed
        const changedFields = [];
        const changes = {};

        Object.entries(contactData).forEach(([key, value]) => {
          if (oldData[key] !== value) {
            changedFields.push(key);
            changes[key] = {
              from: oldData[key],
              to: value
            };
          }
        });

        // Only proceed with update if there are changes
        if (changedFields.length > 0) {
          console.log('Changes detected:', { changedFields, changes });
          
          const startTime = Date.now();
          console.log('Starting update process...');

          // First update the member record
          console.log('Updating member record...');
          const { data: updatedMember, error: updateError } = await supabase
            .from('texting_contacts')
            .update({
              ...contactData,
              updated_at: new Date().toISOString(),
            })
            .eq('id', member.id)
            .select()
            .single();

          if (updateError) {
            console.error('Update error:', updateError);
            throw updateError;
          }

          console.log('Member updated:', { 
            data: updatedMember,
            elapsed: Date.now() - startTime 
          });

          // Now create the audit record
          const { data: { user } } = await supabase.auth.getUser();
          console.log('Creating audit record...');
          const { data: auditData, error: auditError } = await supabase
            .from('contact_audit_log')
            .insert({
              contact_id: member.id,
              action: 'UPDATE',
              changed_by: user?.id,
              old_data: oldData,
              new_data: contactData,
              changed_fields: changedFields,
              changes: changes
            })
            .select();

          if (auditError) {
            console.error('Audit log error:', auditError);
            toast({
              title: 'Warning',
              description: 'Contact was updated but audit log creation failed.',
              status: 'warning',
              duration: 5000,
            });
          } else {
            console.log('Audit log created:', {
              data: auditData,
              elapsed: Date.now() - startTime
            });
          }
          
          savedData = updatedMember;
        } else {
          console.log('No changes detected');
          savedData = member;
        }
      } else {
        // Create new member
        const { data, error } = await supabase
          .from('texting_contacts')
          .insert(contactData)
          .select()
          .single();

        if (error) {
          console.error('Insert error:', error);
          if (error.code === '23505') { // Unique violation
            throw new Error('This phone number is already registered to another contact.');
          }
          throw error;
        }
        savedData = data;

        // Create audit log entry for new member
        const { data: { user } } = await supabase.auth.getUser();
        const { data: auditData, error: auditError } = await supabase
          .from('contact_audit_log')
          .insert({
            contact_id: data.id,
            action: 'INSERT',
            changed_by: user?.id,
            new_data: contactData,
            changed_fields: Object.keys(contactData),
            changes: Object.fromEntries(
              Object.entries(contactData).map(([key, value]) => [
                key,
                { from: null, to: value }
              ])
            )
          })
          .select();

        if (auditError) {
          console.error('Audit log error:', auditError);
          toast({
            title: 'Warning',
            description: 'Contact was created but audit log creation failed.',
            status: 'warning',
            duration: 5000,
          });
        } else {
          console.log('Audit log created:', auditData);
        }
      }

      // Call onSave with the saved data
      if (onSave && savedData) {
        console.log('Saved data:', savedData);
        onSave(savedData);
      }

      // Send welcome email
      try {
        await unifiedEmailService.sendTemplateEmail(
          'welcome_email',
          formData.email,
          {
            firstName: formData.firstName,
            lastName: formData.lastName,
            fullName: `${formData.firstName} ${formData.lastName}`,
            email: formData.email,
            phone: formData.phoneNumber
          },
          { clubName }
        );
      } catch (error) {
        console.error('Failed to send welcome email:', error);
        toast({
          title: 'Failed to send welcome email',
          description: error.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }

      onClose();
    } catch (error) {
      console.error('Form submission error:', error);
      setErrors({
        submit: error.message
      });
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{member ? 'Edit Contact' : 'Add New Contact'}</ModalHeader>
        <ModalBody>
          <Text mb={4} fontSize="sm">
            <Text as="span" color="gray.500">* Indicates</Text>
            <Text as="span" color="red.500"> *</Text>
            <Text as="span" color="gray.500"> required fields</Text>
          </Text>
          <form id="member-form" onSubmit={handleSubmit}>
            <SimpleGrid columns={2} spacing={4} mb={4}>
              <FormControl 
                isRequired 
                isInvalid={errors.firstName}
              >
                <FormLabel>First Name</FormLabel>
                <Input
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                />
                <FormErrorMessage>{errors.firstName}</FormErrorMessage>
              </FormControl>

              <FormControl 
                isRequired 
                isInvalid={errors.lastName}
              >
                <FormLabel>Last Name</FormLabel>
                <Input
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                />
                <FormErrorMessage>{errors.lastName}</FormErrorMessage>
              </FormControl>
            </SimpleGrid>

            <FormControl 
              isRequired 
              isInvalid={errors.phoneNumber}
              mb={4}
            >
              <FormLabel>Phone Number</FormLabel>
              <Input
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
                placeholder="8005551234"
                maxLength={10}
                type="tel"
                pattern="[0-9]*"
              />
              <FormHelperText>
                Enter 10 digits only
                (example: 6125551234)
              </FormHelperText>
              <FormErrorMessage>{errors.phoneNumber}</FormErrorMessage>
            </FormControl>

            <FormControl 
              isRequired
              isInvalid={errors.email}
              mb={4}
            >
              <FormLabel>Email</FormLabel>
              <Input
                name="email"
                value={formData.email}
                onChange={handleChange}
                type="email"
                placeholder="Enter email"
              />
              <FormErrorMessage>{errors.email}</FormErrorMessage>
            </FormControl>
          </form>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button 
            colorScheme="blue" 
            type="submit"
            form="member-form"
            isLoading={loading}
          >
            {member ? 'Save Changes' : 'Add Contact'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default MemberModal;
